import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { UserAvailability } from 'core/interfaces/userInterface';
import { ShiftRequest } from 'types/shift-requests';
import {
	AvailabilityChangeKind,
	WeekDay,
	buildAvailabilityComparisonRows,
} from '../../utils/availabilityDisplay';
import styles from './AvailabilityComparison.module.scss';

interface AvailabilityComparisonProps {
	days: WeekDay[];
	currentAvailability: UserAvailability[];
	relatedRequests: ShiftRequest[];
}

const CHANGE_LABEL: Record<Exclude<AvailabilityChangeKind, 'unchanged'>, string> = {
	changed: 'Changed',
	added: 'Added',
	removed: 'Removed',
};

const AvailabilityComparison: React.FC<AvailabilityComparisonProps> = ({
	days,
	currentAvailability,
	relatedRequests,
}) => {
	const rows = useMemo(
		() => buildAvailabilityComparisonRows(days, currentAvailability, relatedRequests),
		[days, currentAvailability, relatedRequests]
	);
	const changeCount = rows.filter((row) => row.changeKind !== 'unchanged').length;

	return (
		<Box>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: 2,
					mb: 2,
				}}
			>
				<Box>
					<Typography variant="h6" gutterBottom>
						Current vs Requested Availability
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Existing schedule compared with the requested changes
					</Typography>
				</Box>
				{changeCount > 0 && (
					<span className={styles.changeCount}>
						{changeCount} {changeCount === 1 ? 'change' : 'changes'}
					</span>
				)}
			</Box>
			<div className={styles.card}>
			<div className={styles.columns} aria-hidden="true">
				<span>Day</span>
				<span>Current</span>
				<span />
				<span>Requested</span>
				<span />
			</div>
			{rows.map((row) => {
				const changeKind = row.changeKind;

				return (
					<div
						key={row.dayId}
						className={`${styles.row} ${
							changeKind === 'unchanged' ? styles.rowUnchanged : styles.rowChanged
						}`}
					>
						<span className={styles.day}>{row.dayShort}</span>
						<span className={styles.current}>{row.currentLabel}</span>
						<span className={styles.arrow} aria-hidden="true">
							{changeKind === 'unchanged' ? '' : '→'}
						</span>
						<span
							className={`${styles.requested} ${
								changeKind === 'unchanged' ? '' : styles.requestedChanged
							}`}
						>
							{row.requestedLabel}
						</span>
						{changeKind === 'unchanged' ? (
							<span />
						) : (
							<span
								className={`${styles.badge} ${
									changeKind === 'added'
										? styles.badgeAdded
										: changeKind === 'removed'
											? styles.badgeRemoved
											: styles.badgeChanged
								}`}
							>
								{CHANGE_LABEL[changeKind]}
							</span>
						)}
					</div>
				);
			})}
			</div>
		</Box>
	);
};

export default AvailabilityComparison;
